// Componenti grafici riutilizzati: AreaChart con asse Y, Sparkline.
import { cn } from "@v2/lib/utils";
import { smoothPath } from "../lib/svg";

export function AreaChart({
  values,
  height = 180,
  color = "hsl(var(--accent))",
  labels,
  formatY,
}: {
  values: number[];
  height?: number;
  color?: string;
  labels?: string[];
  formatY?: (v: number) => string;
}) {
  if (values.length < 2) {
    return <div className="text-xs text-muted-foreground py-6 text-center">Dati insufficienti.</div>;
  }
  const w = 720;
  const h = height;
  const padL = 36;
  const padR = 4;
  const innerTop = 12;
  const innerBottom = h - 8;
  const innerH = innerBottom - innerTop;
  const innerW = w - padR;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => ({
    x: padL + (i / (values.length - 1)) * (innerW - padL),
    y: innerBottom - ((v - min) / range) * innerH,
  }));
  const path = smoothPath(points, 0.7);
  const area = `${path} L ${innerW} ${h} L ${padL} ${h} Z`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    y: innerTop + t * innerH,
    pct: (innerTop + t * innerH) / h,
    val: max - t * range,
  }));
  const fmt = formatY || ((v: number) => {
    if (Math.abs(v) >= 100) return v.toFixed(0);
    if (Math.abs(v) >= 10) return v.toFixed(1);
    return v.toFixed(2);
  });
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height={h}>
        {ticks.map((t, idx) => (
          <line
            key={idx}
            x1={padL}
            x2={innerW}
            y1={t.y}
            y2={t.y}
            stroke="hsl(var(--border))"
            strokeDasharray={idx === ticks.length - 1 ? undefined : "2 4"}
          />
        ))}
        <path d={area} fill={color} fillOpacity={0.1} />
        <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* Label asse Y in HTML per evitare lo stretch del viewBox preserveAspectRatio="none" */}
      <div className="absolute inset-y-0 left-0 w-9 pointer-events-none">
        {ticks.map((t, idx) => (
          <span
            key={idx}
            className="font-mono uppercase tracking-wider text-xs text-muted-foreground absolute right-1.5 -translate-y-1/2"
            style={{ top: `${t.pct * 100}%` }}
          >
            {fmt(t.val)}
          </span>
        ))}
      </div>
      {labels && labels.length > 0 && (
        <div className="flex justify-between text-xs text-muted-foreground mt-1 font-mono uppercase tracking-wider">
          {labels.map((label, idx) => (
            <span key={idx}>{label}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sparkline({
  values,
  color = "currentColor",
  className,
}: {
  values: number[];
  color?: string;
  className?: string;
}) {
  if (values.length < 2) return null;
  const w = 60;
  const h = 18;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => ({
    x: (i / (values.length - 1)) * w,
    y: h - ((v - min) / range) * (h - 2) - 1,
  }));
  const d = smoothPath(points, 0.7);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={cn("w-16 h-5", className)} preserveAspectRatio="none">
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Radar chart minimale per confronto multi-player. Ogni dataset ha label +
// values[] in scala 0-100 corrispondenti agli axes[]. Renderizza in SVG puro
// (zero dipendenze). Pensato per 2-4 dataset, 4-8 assi.
export type RadarDataset = { label: string; values: number[]; color: string };

export function RadarChart({
  axes,
  datasets,
  size = 320,
  className,
}: {
  axes: string[];
  datasets: RadarDataset[];
  size?: number;
  className?: string;
}) {
  // Padding extra nel viewBox per non tagliare le etichette degli assi quando
  // sono lunghe (es. "Passaggi %", "Goal/match"): le label sono ~12% oltre il
  // radius e il text-anchor cambia con il quadrante, può sforare i bordi.
  const pad = size * 0.22;
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.32;
  const n = axes.length;
  if (n < 3 || datasets.length === 0) {
    return <div className="text-xs text-muted-foreground py-6 text-center">Dati insufficienti per il radar.</div>;
  }
  // Per ogni asse calcolo l'angolo (alto = 0, in senso orario).
  const angleFor = (i: number) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const pointAt = (i: number, scale: number) => {
    const a = angleFor(i);
    return [cx + Math.cos(a) * radius * scale, cy + Math.sin(a) * radius * scale];
  };
  const polyForValues = (values: number[]) =>
    values
      .map((v, i) => {
        const s = Math.max(0, Math.min(1, v / 100));
        const [x, y] = pointAt(i, s);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  const grid = [0.25, 0.5, 0.75, 1];
  return (
    <svg
      viewBox={`${-pad} ${-pad} ${size + pad * 2} ${size + pad * 2}`}
      className={cn("w-full h-auto", className)}
    >
      {/* Griglia poligonale */}
      {grid.map((scale, gi) => (
        <polygon
          key={gi}
          points={Array.from({ length: n }, (_, i) => {
            const [x, y] = pointAt(i, scale);
            return `${x.toFixed(2)},${y.toFixed(2)}`;
          }).join(" ")}
          fill="none"
          stroke="hsl(var(--border))"
          strokeOpacity={0.5}
          strokeWidth={0.8}
        />
      ))}
      {/* Linee radiali */}
      {axes.map((_, i) => {
        const [x, y] = pointAt(i, 1);
        return (
          <line
            key={`axis-${i}`}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke="hsl(var(--border))"
            strokeOpacity={0.5}
            strokeWidth={0.8}
          />
        );
      })}
      {/* Polygons dataset */}
      {datasets.map((ds, di) => (
        <g key={`ds-${di}`}>
          <polygon points={polyForValues(ds.values)} fill={ds.color} fillOpacity={0.18} stroke={ds.color} strokeWidth={1.6} />
          {ds.values.map((v, i) => {
            const s = Math.max(0, Math.min(1, v / 100));
            const [x, y] = pointAt(i, s);
            return <circle key={i} cx={x} cy={y} r={2.4} fill={ds.color} />;
          })}
        </g>
      ))}
      {/* Etichette assi */}
      {axes.map((label, i) => {
        const [x, y] = pointAt(i, 1.12);
        const a = angleFor(i);
        const anchor = Math.abs(Math.cos(a)) < 0.2 ? "middle" : Math.cos(a) > 0 ? "start" : "end";
        return (
          <text
            key={`label-${i}`}
            x={x}
            y={y}
            textAnchor={anchor}
            dominantBaseline="middle"
            fontSize={10}
            fill="hsl(var(--muted-foreground))"
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}
